import type { EditorView } from "@codemirror/view";
import { EditorSelection } from "@codemirror/state";

export type SnippetGroup = "basic" | "block" | "layout" | "util";

export interface SnippetDef {
  key: string;
  label: string;
  group: SnippetGroup;
  insert?: (view: EditorView) => void;
  dropdown?: { label: string; insert: (view: EditorView) => void }[];
}

interface InsertOptions {
  template: (sel: string, ctx: { maxStep: number; maxImg: number }) => string;
  selectPlaceholder?: (inserted: string) => { from: number; to: number } | null;
}

function getMaxMatch(doc: string, re: RegExp): number {
  let max = 0;
  for (const m of doc.matchAll(re)) {
    const n = parseInt(m[1], 10);
    if (!Number.isNaN(n) && n > max) max = n;
  }
  return max;
}

function insertSnippet(view: EditorView, opts: InsertOptions) {
  const selRange = view.state.selection.main;
  const sel = view.state.sliceDoc(selRange.from, selRange.to);
  const doc = view.state.doc.toString();
  const maxStep = getMaxMatch(doc, /<span class="step-num">(\d+)/g);
  const maxImg = getMaxMatch(doc, /\[IMG-(\d+)\]/g);

  const inserted = opts.template(sel, { maxStep, maxImg });
  const hasSelection = sel.length > 0;
  const from = selRange.from;
  const to = selRange.to;

  const ph = hasSelection ? null : opts.selectPlaceholder?.(inserted) ?? null;
  const selection = ph
    ? EditorSelection.range(from + ph.from, from + ph.to)
    : EditorSelection.cursor(from + inserted.length);

  view.dispatch({
    changes: { from, to, insert: inserted },
    selection,
    userEvent: "input.snippet",
  });
  view.focus();
}

function findPlaceholder(text: string, needle: string): { from: number; to: number } | null {
  const i = text.indexOf(needle);
  return i >= 0 ? { from: i, to: i + needle.length } : null;
}

function makeTipInserter(color: string, emoji: string, title: string) {
  return (view: EditorView) =>
    insertSnippet(view, {
      template: (sel) =>
        `<div class="tip tip-${color}"><div class="tip-title">${emoji} ${title}</div>${sel || "提示内容"}</div>`,
      selectPlaceholder: (txt) => findPlaceholder(txt, "提示内容"),
    });
}

export const SNIPPETS: SnippetDef[] = [
  {
    key: "h2",
    label: "H",
    group: "basic",
    insert: (view) =>
      insertSnippet(view, {
        template: (sel) => (sel ? `<h2>${sel}</h2>` : `<h2>标题</h2>`),
        selectPlaceholder: (txt) => findPlaceholder(txt, "标题"),
      }),
  },
  {
    key: "p",
    label: "P",
    group: "basic",
    insert: (view) =>
      insertSnippet(view, {
        template: (sel) => (sel ? `<p>${sel}</p>` : `<p>正文</p>`),
        selectPlaceholder: (txt) => findPlaceholder(txt, "正文"),
      }),
  },
  {
    key: "step",
    label: "步",
    group: "block",
    insert: (view) =>
      insertSnippet(view, {
        template: (sel, { maxStep }) =>
          `<div class="step"><span class="step-num">${maxStep + 1}</span>${sel || "步骤说明"}</div>`,
        selectPlaceholder: (txt) => findPlaceholder(txt, "步骤说明"),
      }),
  },
  {
    key: "tip",
    label: "提",
    group: "block",
    dropdown: [
      { label: "ℹ 信息", insert: makeTipInserter("info", "ℹ", "信息") },
      { label: "✓ 成功", insert: makeTipInserter("success", "✓", "成功") },
      { label: "⚠ 注意", insert: makeTipInserter("warning", "⚠", "注意") },
      { label: "✗ 警示", insert: makeTipInserter("danger", "✗", "警示") },
    ],
  },
  {
    key: "card",
    label: "卡",
    group: "block",
    insert: (view) =>
      insertSnippet(view, {
        template: (sel) =>
          sel
            ? `<div class="clean-card">${sel}</div>`
            : `<div class="clean-card"><h3>卡片标题</h3><p>内容</p></div>`,
        selectPlaceholder: (txt) => findPlaceholder(txt, "卡片标题"),
      }),
  },
  {
    key: "table",
    label: "表",
    group: "block",
    insert: (view) =>
      insertSnippet(view, {
        template: () =>
          `<table>\n  <thead><tr><th>列 1</th><th>列 2</th><th>列 3</th></tr></thead>\n  <tbody>\n    <tr><td>—</td><td>—</td><td>—</td></tr>\n    <tr><td>—</td><td>—</td><td>—</td></tr>\n  </tbody>\n</table>`,
      }),
  },
  {
    key: "grid2",
    label: "网2",
    group: "layout",
    insert: (view) =>
      insertSnippet(view, {
        template: () =>
          `<div class="grid-2">\n  <div class="clean-card">A</div>\n  <div class="clean-card">B</div>\n</div>`,
      }),
  },
  {
    key: "grid3",
    label: "网3",
    group: "layout",
    insert: (view) =>
      insertSnippet(view, {
        template: () =>
          `<div class="grid-3">\n  <div class="clean-card">A</div>\n  <div class="clean-card">B</div>\n  <div class="clean-card">C</div>\n</div>`,
      }),
  },
  {
    key: "badge",
    label: "徽",
    group: "util",
    insert: (view) =>
      insertSnippet(view, {
        template: (sel) => (sel ? `<span class="badge">${sel}</span>` : `<span class="badge">标签</span>`),
        selectPlaceholder: (txt) => findPlaceholder(txt, "标签"),
      }),
  },
  {
    key: "kv",
    label: "键",
    group: "util",
    insert: (view) =>
      insertSnippet(view, {
        template: () =>
          `<dl class="kv-list">\n  <dt>键</dt><dd>值</dd>\n  <dt>键</dt><dd>值</dd>\n</dl>`,
      }),
  },
  {
    key: "divider",
    label: "分",
    group: "util",
    insert: (view) =>
      insertSnippet(view, {
        template: () => `<hr class="divider">`,
      }),
  },
  {
    key: "img",
    label: "图",
    group: "util",
    insert: (view) =>
      insertSnippet(view, {
        template: (_sel, { maxImg }) => `[IMG-${maxImg + 1}]`,
      }),
  },
];
